import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createAllergySchema,
  updateAllergySchema,
  type CreateAllergyInput,
} from "@shared/validation/allergy.schema";
import { SEVERITY_LEVELS, SEVERITY_LABELS } from "@shared/constants/severity";
import { useCreateAllergy, useUpdateAllergy, type AllergySummary } from "../../lib/api/useAllergies";
import { useMedications } from "../../lib/api/useMedications";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { Button } from "../ui/Button";

interface AllergyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  allergy?: AllergySummary;
}

export function AllergyDialog({ open, onOpenChange, childId, allergy }: AllergyDialogProps) {
  const isEdit = Boolean(allergy);
  const createMutation = useCreateAllergy(childId);
  const updateMutation = useUpdateAllergy(childId, allergy?.id ?? "");
  const activeMutation = isEdit ? updateMutation : createMutation;
  const { publish } = useToast();
  // Rescue-medication options come from THIS child's medications only — the
  // server re-checks the same binding, so a stale list can never link across
  // children (see AllergiesService.assertRescueMedicationBelongsToChild).
  const { data: medications } = useMedications(childId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateAllergyInput>({
    // Use the update schema on edit (looser — all fields optional) to avoid
    // drift if the server ever adds a required field to create.
    resolver: zodResolver(isEdit ? updateAllergySchema : createAllergySchema),
    defaultValues: {
      name: allergy?.name ?? "",
      severity: allergy?.severity ?? "mild",
      description: allergy?.description ?? null,
      emergencyAction: allergy?.emergencyAction ?? null,
      reactionSymptoms: allergy?.reactionSymptoms ?? null,
      treatment: allergy?.treatment ?? null,
      epinephrineLocation: allergy?.epinephrineLocation ?? null,
      rescueMedicationId: allergy?.rescueMedicationId ?? null,
      notes: allergy?.notes ?? null,
    },
  });

  // Run the reset only on the closed→open TRANSITION, not on every render while
  // open: createMutation/updateMutation are new object identities each render and
  // mutation.reset() notifies subscribers, so an unguarded effect re-runs forever
  // and wipes anything the user types (pre-existing bug, found in the reskin QA).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset({
        name: allergy?.name ?? "",
        severity: allergy?.severity ?? "mild",
        description: allergy?.description ?? null,
        emergencyAction: allergy?.emergencyAction ?? null,
        reactionSymptoms: allergy?.reactionSymptoms ?? null,
        treatment: allergy?.treatment ?? null,
        epinephrineLocation: allergy?.epinephrineLocation ?? null,
        rescueMedicationId: allergy?.rescueMedicationId ?? null,
        notes: allergy?.notes ?? null,
      });
      // Clear any stale error banner from the previous open/close cycle.
      createMutation.reset();
      updateMutation.reset();
    }
    wasOpen.current = open;
  }, [open, allergy, reset, createMutation, updateMutation]);

  const onSubmit = async (data: CreateAllergyInput) => {
    try {
      await activeMutation.mutateAsync(data);
      publish({
        variant: "success",
        title: isEdit ? "Allergy updated successfully" : "Allergy added successfully",
      });
      onOpenChange(false);
    } catch (err) {
      publish({
        variant: "error",
        title: isEdit ? "Failed to update allergy" : "Failed to add allergy",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={isEdit ? "Edit Allergy" : "Add Allergy"}
      description={
        isEdit
          ? "Update the allergy information below."
          : "Add a new allergy or medical restriction with severity level and emergency actions."
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Field label="Allergy Name *" htmlFor="allergy-name" error={errors.name?.message}>
          <input
            id="allergy-name"
            type="text"
            {...register("name")}
            className={INPUT_CLASS}
            placeholder="e.g., Peanuts, Dairy, Pollen"
          />
        </Field>

        <Field label="Severity *" htmlFor="allergy-severity" error={errors.severity?.message}>
          <select id="allergy-severity" {...register("severity")} className={INPUT_CLASS}>
            {SEVERITY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {SEVERITY_LABELS[level]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Description" htmlFor="allergy-description">
          <textarea
            id="allergy-description"
            rows={2}
            {...register("description")}
            className={INPUT_CLASS}
            placeholder="Describe the allergy and its effects..."
          />
        </Field>

        {/*
          Emergency care plan — modelled on FARE's Food Allergy & Anaphylaxis
          Emergency Care Plan: symptoms, then treatment, then where the device
          is. Kept as three separate fields (not one blob) so the caregiver view
          can render them in a fixed order under stress.
        */}
        <fieldset className="space-y-4 rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
          <legend className="px-1 text-sm font-medium text-rose-700">
            Emergency care plan
          </legend>

          <Field label="Reaction Symptoms" htmlFor="allergy-symptoms">
            <textarea
              id="allergy-symptoms"
              rows={2}
              {...register("reactionSymptoms")}
              className={INPUT_CLASS}
              placeholder="What a reaction looks like for this child — hives, swelling, wheezing…"
            />
          </Field>

          <Field label="Treatment" htmlFor="allergy-treatment">
            <textarea
              id="allergy-treatment"
              rows={2}
              {...register("treatment")}
              className={INPUT_CLASS}
              placeholder="Step by step. For a severe reaction, use epinephrine first, then call 911."
            />
          </Field>

          <Field label="Rescue Medication" htmlFor="allergy-rescue-med">
            <select
              id="allergy-rescue-med"
              {...register("rescueMedicationId")}
              className={INPUT_CLASS}
            >
              <option value="">None selected</option>
              {(medications ?? []).map((med) => (
                <option key={med.id} value={med.id}>
                  {med.name} — {med.dosage}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-600">
              Link the auto-injector or rescue medication that treats this allergy. A
              severe allergy with nothing linked triggers a reminder.
            </p>
          </Field>

          <Field label="Where the device is kept" htmlFor="allergy-epi-location">
            <input
              id="allergy-epi-location"
              type="text"
              {...register("epinephrineLocation")}
              className={INPUT_CLASS}
              placeholder="e.g., front pocket of the blue backpack"
            />
          </Field>
        </fieldset>

        <Field label="Emergency Action" htmlFor="allergy-emergency">
          <textarea
            id="allergy-emergency"
            rows={2}
            {...register("emergencyAction")}
            className={INPUT_CLASS}
            placeholder="Anything else a caregiver should know."
          />
        </Field>

        <Field label="Additional Notes" htmlFor="allergy-notes">
          <textarea
            id="allergy-notes"
            rows={2}
            {...register("notes")}
            className={INPUT_CLASS}
          />
        </Field>

        {activeMutation.error instanceof ApiError && (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {activeMutation.error.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || activeMutation.isPending}>
            {activeMutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Allergy"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
